import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import dayjs from "dayjs";
import db from "@/db";
import crudGenerator from "@/api/utils/offlineSync";
import {
  isAttributedFailure,
  pendingProjects,
  pendingRecords,
} from "@/db/pendingSync";
import { pendingPageOf } from "@/db/pendingRecord";
import {
  isNetworkError,
  isOfflineFailure,
  isTransientFailure,
} from "@/utils/network";
import { reportDeviceDataError } from "@/utils/deviceDataError";
import { useProjectsStore } from "@/store/modules/projects";
import { useNetworkStore } from "@/composables/network/useNetwork";
import {
  KEPT_NOTICE_KEY,
  OFFLINE_FALLBACK_ROUTE,
  notifyOfflineFailure,
  resetKeptNotice,
} from "@/composables/network/offlineMode";
import {
  flushSyncProblems,
  queueSyncProblem,
} from "@/composables/network/syncReport";
import {
  dismissNotification,
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from "@/composables/useNotifications";
import {
  endSyncRun,
  openSyncDialog,
  startSyncRun,
} from "@/composables/network/syncProgress";
import { i18nGlobal as i18n } from "@/setup/i18n";
import { sessionExpired } from "@/setup/oidcSessionRecovery";

const RETRY_INTERVAL_MS = 60 * 1000;
const RETRY_SOON_MS = 10 * 1000;
const LOCK_NAME = "atmo-pending-sync";
const LOCK_BUSY = Symbol("lockBusy");
const SENT_TOAST_WINDOW_MS = 5 * 1000;

const loggedFailures = new Set();
const notifiedRejections = new Set();
const shownRejections = new Set();
let rejectedRetried = false;
let running = null;
let soonTimer = null;
let sentToastAt = 0;
let reportMode = "toasts";

export const screenReloads = ref(0);

const announce = (show, message) => {
  dismissNotification(KEPT_NOTICE_KEY);
  show(i18n.t(message));
};

const announceSent = (message) => {
  if (Date.now() - sentToastAt < SENT_TOAST_WINDOW_MS) return;
  sentToastAt = Date.now();
  announce(showSuccessNotification, message);
};

const missionLabel = async (projectUuid) => {
  if (useProjectsStore().currentProjectUuid === projectUuid) return "";
  const project = await db.projects.get(projectUuid);
  const mission = project?.projectName || project?.chronorapso || "";
  return ` ${i18n.t("onMission", { mission })}`;
};

const archiveSkeleton = async (uuid) => {
  const { useRecordBooksStore } = await import("@/store/modules/recordBooks");
  await useRecordBooksStore().setRecordBooksArchived([uuid], true);
};

const discardOne = async (row) => {
  const model = Object.values(db.models).find(
    ({ tableName }) => tableName === row.table
  );
  if (row.skeleton) await archiveSkeleton(row.uuid);
  await crudGenerator(model).discardKept(row.uuid);
};

const leaveDiscardedScreen = async (rows) => {
  const { default: router } = await import("@/router");
  const route = router.currentRoute.value;
  const isOpenRecordBook = ({ table, uuid }) =>
    table === "recordBooks" && route.params.recordBookUuid === uuid;
  if (rows.some(isOpenRecordBook)) {
    await router.replace({
      name: OFFLINE_FALLBACK_ROUTE,
      params: { projectId: route.params.projectId },
    });
    return;
  }
  if (rows.some((row) => route.path === pendingPageOf(row).split("?")[0]))
    screenReloads.value += 1;
};

export const discardStuck = async (rows) => {
  const keys = rows.map(({ key }) => key);
  try {
    await rows.reduce(
      (previous, row) => previous.then(() => discardOne(row)),
      Promise.resolve()
    );
  } catch (error) {
    if (isOfflineFailure(error)) {
      notifyOfflineFailure();
      return;
    }
    console.error("Kept change could not be discarded", keys, error);
    reportDeviceDataError(error, {
      risk: "discard_failed",
      table: rows[0]?.table,
      keys,
    });
    showErrorNotification(i18n.t("changeNotDiscarded"));
    return;
  }
  keys.forEach((key) => {
    shownRejections.delete(key);
    dismissNotification(key);
  });
  showSuccessNotification(i18n.t("changeDiscarded"));
  await leaveDiscardedScreen(rows);
};

const isStuck = ({ rejected, failed }) => !!(rejected || failed);

const STUCK_MESSAGES = {
  recordBooks: {
    rejected: ["recordBookSyncFailed", "recordBookSyncFailedDetail"],
    failed: ["recordBookSendFailed"],
  },
  other: {
    rejected: ["pendingChangeRejected", "pendingChangeRejectedDetail"],
    failed: ["pendingChangeSendFailed"],
  },
};

const stuckMessage = async (row, where) => {
  const kind = row.rejected ? "rejected" : "failed";
  const detail = row.rejected?.detail;
  const [plain, detailed] =
    STUCK_MESSAGES[row.table === "recordBooks" ? "recordBooks" : "other"][kind];
  const label =
    row.table === "recordBooks"
      ? ((await db.recordBooks.get(row.uuid))?.label ?? "")
      : "";
  const date = dayjs(row.at).format("DD/MM/YYYY HH:mm");
  return i18n.t(detail ? detailed : plain, { label, date, detail, where });
};

const stuckActions = (row, page) => [
  {
    label: i18n.t("discardChange"),
    variant: "text",
    handler: () => discardStuck([row]),
    confirm: {
      title: i18n.t("discardChangeTitle"),
      text: i18n.t("discardChangeText"),
      cancel: i18n.t("keepChange"),
    },
  },
  { label: i18n.t("openPage"), to: page },
];

const notifyStuck = async (projectUuid) => {
  const notifiedKey = (row) =>
    row.key + row.at + (row.rejected ? "rejected" : "failed");
  const fresh = (await pendingRecords(projectUuid)).filter(
    (row) => isStuck(row) && !notifiedRejections.has(notifiedKey(row))
  );
  fresh.forEach((row) => notifiedRejections.add(notifiedKey(row)));
  const toasted =
    reportMode === "toasts" ? fresh.filter((row) => !row.inOfflineMode) : [];
  if (!toasted.length) return;
  const where = await missionLabel(projectUuid);
  for (const row of toasted) {
    const page = pendingPageOf(row);
    const message = await stuckMessage(row, where);
    dismissNotification(KEPT_NOTICE_KEY);
    dismissNotification(row.key);
    await nextTick();
    shownRejections.add(row.key);
    showErrorNotification(message, {
      key: row.key,
      timeout: -1,
      actions: stuckActions(row, page),
    });
  }
};

const dismissResolvedRejections = async () => {
  const keys = [...shownRejections];
  const rows = await db.pendingSyncs.bulkGet(keys);
  keys.forEach((key, index) => {
    if (rows[index] && isStuck(rows[index])) return;
    shownRejections.delete(key);
    dismissNotification(key);
  });
};

const logFailureOnce = (projectUuid, error) => {
  if (isTransientFailure(error) || loggedFailures.has(projectUuid)) return;
  loggedFailures.add(projectUuid);
  console.error("Pending changes could not be sent", projectUuid, error);
  if (isAttributedFailure(error)) return;
  queueSyncProblem({ kind: "failed", projectUuid, rows: [], error });
};

const waitingRows = () =>
  db.pendingSyncs.filter((row) => !row.rejected).toArray();

const pushEachProject = async ({ retryRejected, quiet }) => {
  const projectsStore = useProjectsStore();
  for (const projectUuid of await pendingProjects()) {
    try {
      await projectsStore.pushPendingChanges(projectUuid, {
        retryRejected: retryRejected || !rejectedRetried,
      });
    } catch (error) {
      if (isNetworkError(error)) return;
      logFailureOnce(projectUuid, error);
    }
    await notifyStuck(projectUuid);
  }
  rejectedRetried = true;
  if (
    !(await waitingRows()).length &&
    resetKeptNotice() &&
    !quiet &&
    !(await db.pendingSyncs.count())
  )
    announceSent("syncDone");
  await dismissResolvedRejections();
};

const pushPendingProjects = async (options) => {
  startSyncRun((await db.pendingSyncs.toArray()).map(({ key }) => key));
  try {
    await pushEachProject(options);
  } finally {
    endSyncRun();
    await flushSyncProblems();
  }
};

const withTabLock = (task) => {
  const locks = globalThis.navigator.locks;
  if (!locks) return task();
  return locks.request(LOCK_NAME, { ifAvailable: true }, (lock) =>
    lock ? task() : LOCK_BUSY
  );
};

export const syncPendingProjects = ({
  retryRejected = false,
  quiet = false,
} = {}) => {
  const { isOnline, isOfflineMode } = useNetworkStore();
  if (!isOnline || isOfflineMode) return running;
  if (running && retryRejected)
    return running.then(() => syncPendingProjects({ retryRejected, quiet }));
  if (running) return running;
  running = withTabLock(() =>
    pushPendingProjects({ retryRejected, quiet })
  ).finally(() => {
    running = null;
  });
  return running;
};

const pushAndReport = async (keys, doneMessage) => {
  if (
    (await syncPendingProjects({ retryRejected: true, quiet: true })) ===
    LOCK_BUSY
  ) {
    showWarningNotification(i18n.t("syncInOtherTab"));
    return;
  }
  const left = (await db.pendingSyncs.bulkGet(keys)).filter(Boolean);
  const stuck = left.filter(isStuck);
  if (stuck.length) {
    if (reportMode === "summary") {
      dismissNotification(KEPT_NOTICE_KEY);
      openSyncDialog();
    }
    return;
  }
  if (left.length)
    announce(
      showWarningNotification,
      sessionExpired.value ? "syncNeedsLogin" : "syncIncomplete"
    );
  else announceSent(doneMessage);
};

const withReportMode = async (mode, task) => {
  reportMode = mode;
  try {
    await task();
  } finally {
    reportMode = "toasts";
  }
};

export const syncAfterOfflineMode = async () => {
  const keys = (await db.pendingSyncs.toArray()).map(({ key }) => key);
  if (!keys.length) {
    if (globalThis.navigator.onLine)
      showSuccessNotification(i18n.t("backOnline"));
    return;
  }
  await withReportMode("summary", () => pushAndReport(keys, "syncDone"));
};

export const syncPendingNow = async ({ silent = false } = {}) => {
  const keys = (await db.pendingSyncs.toArray()).map(({ key }) => key);
  notifiedRejections.clear();
  await withReportMode(silent ? "silent" : "toasts", () =>
    pushAndReport(keys, "syncNowDone")
  );
};

const dismissSyncNotifications = () => {
  [KEPT_NOTICE_KEY, ...shownRejections].forEach((key) =>
    dismissNotification(key)
  );
  shownRejections.clear();
};

export const pushPendingSoon = () => {
  clearTimeout(soonTimer);
  soonTimer = setTimeout(syncPendingProjects, RETRY_SOON_MS);
};

export default function usePendingSync() {
  const networkStore = useNetworkStore();
  let timer = null;

  watch(
    () => networkStore.isOnline,
    (isOnline) => isOnline && syncPendingProjects()
  );

  watch(
    () => networkStore.isOfflineMode,
    (isOfflineMode) => isOfflineMode && dismissSyncNotifications()
  );

  onMounted(() => {
    timer = setInterval(syncPendingProjects, RETRY_INTERVAL_MS);
    syncPendingProjects();
  });

  onUnmounted(() => clearInterval(timer));
}
